import { faCircleCheck, faCircleX, faClock, faCloudCheck } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Box, Chip, styled, Tooltip, TooltipProps, Typography } from "@mui/material";
import { OnlinePatientManagementContext } from "@ngt/opms";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { ImageDocument, ImageDocumentGetSingleById } from "../../api/dtos";
import { AppSettings } from "../../AppSettings";
import { ImageUploadUpdaterContext } from "../../contexts/ImageUploadUpdaterContext";

const RedFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.redLight} !important` :
    `${AppSettings.palette.redDark} !important`,
}));

const GreenFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.greenLight} !important` :
    `${AppSettings.palette.greenDark} !important`, 
}));

const BlueFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.blueLight} !important` :
    `${AppSettings.palette.blueDark} !important`,
}));

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
    theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
}));

const TableButtonsBox = styled(Box)(() => ({
    display: 'flex',
    gap: 8,
    height: '100%',
    alignItems: 'center',
}));

const EmptyTypography = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    color: theme.palette.text.disabled,
}));

interface IImageCell {
    documents: ImageDocument[] | undefined;
}

const GlanceImageCell: FC<IImageCell> = ({
    documents,
}) => {
    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);

    const [imageDocuments, setImageDocuments] = useState<ImageDocument[]>(documents ?? []);

    const imageUploadUpdater = useContext(ImageUploadUpdaterContext);

    // Keep local state in sync 
    useEffect(() => {
        setImageDocuments(documents ?? []);
    }, [documents]);

    // Subscribe to every document 
    const documentIds = useMemo(
        () => (documents ?? []).map(doc => doc.id).filter((id): id is number => id != null),
        [documents]
    );

    useEffect(() => {
        if (documentIds.length === 0) {
            return;
        }

        const unsubscribes = documentIds.map(id =>
            imageUploadUpdater.subscribe(id, async () => {
                const response = await client.get(
                    new ImageDocumentGetSingleById({ id })
                );

                setImageDocuments(prev =>
                    prev.map(doc => (doc.id === id ? response.form : doc))
                );
            })
        );

        return () => {
            unsubscribes.forEach(unsubscribe => unsubscribe());
        };
    }, [imageUploadUpdater, client, documentIds]);


    const failedDocs = imageDocuments?.filter(doc => doc.deIdentifiedFailed === true) ?? [];
    const verifiedDocs = imageDocuments?.filter(doc => doc.isVerified === true) ?? [];
    const pendingDocs = imageDocuments?.filter(doc => 
        doc.isVerified !== true &&
        doc.deIdentifiedFailed !== true && 
        doc.isUploadedToSharePoint !== true
    ) ?? [];
    const legacyDocs = imageDocuments?.filter(doc => doc.isUploadedToSharePoint === true) ?? [];

    if (imageDocuments.length === 0) {
        return (
            <EmptyTypography variant="body2">
                No uploads
            </EmptyTypography>
        );
    }

    return (
        <TableButtonsBox>
            {legacyDocs.length > 0 ? (
                <StyledTooltip title="Legacy uploads.">
                    <Chip
                        icon={<GreenFontAwesomeIconNoHover icon={faCloudCheck} />}
                        label={legacyDocs.length}
                        size="small"
                        sx={{ width: 50, height: 30 }}
                    />
                </StyledTooltip>
            ) : (
                <>
                    {pendingDocs.length > 0 && (
                        <StyledTooltip title="Awaiting verification.">
                            <Chip
                                icon={<BlueFontAwesomeIconNoHover icon={faClock} />}
                                label={pendingDocs.length}
                                size="small"
                                sx={{ width: 50, height: 30 }}
                            />
                        </StyledTooltip>
                    )}
                    {verifiedDocs.length > 0 && (
                        <StyledTooltip title="Verified.">
                            <Chip
                                icon={<GreenFontAwesomeIconNoHover icon={faCircleCheck} />}
                                label={verifiedDocs.length}
                                size="small"
                                sx={{ width: 50, height: 30 }}
                            />
                        </StyledTooltip>
                    )}
                    {failedDocs.length > 0 && (
                        <StyledTooltip title="One or more De-identifications have failed.">
                            <Chip
                                icon={<RedFontAwesomeIconNoHover icon={faCircleX} />}
                                label={failedDocs.length}
                                size="small"
                                sx={{ width: 50, height: 30 }}
                                variant="filled"
                            />
                        </StyledTooltip>
                    )}
                </>
            )}
        </TableButtonsBox>
    );
}

export default GlanceImageCell;
